import Card, { CardProps } from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { useTranslation, Trans } from 'react-i18next';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import { Chip, TextV2 } from '@insights-ltd/design-library/components';
import { formatDateStyle2 } from '@insights-ltd/design-library/utils';
import { Box, styled, useTheme } from '@mui/material';
import type { Pronoun } from 'domain/pronouns';
import { Dialects } from 'types/dialects';
import PronounsDialectLabel from 'components/PronounsDialectLabel';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';

export type DfcProps = {
  className?: string;
  id?: string;
  fullName?: string;
  email?: string;
  editMode: boolean;
  checked: boolean;
  setChecked: (checked: boolean) => void;
  disabled?: boolean;
  pronoun?: Pronoun;
  dialect?: Dialects;
  showPronounsAndDialect?: boolean;
  profileLastSentAt?: string;
  showLastSharedDate?: boolean;
  contributors?: { contributorsCompleted: number; contributersTotal: number };
  showNewContributors?: boolean;
  newContributors?: number;
  showContributorsIncluded?: boolean;
  contributorsIncluded?: number;
  showCost?: boolean;
  cost?: number;
};

const StyledCard = styled(Card)<CardProps>(({ theme }) => ({
  padding: '0 !important',
  borderTop: `2px solid ${theme.palette.blue.main}`,
  borderLeft: `2px solid ${theme.palette.blue.main}`,
  borderRight: `2px solid ${theme.palette.blue.main}`,
}));

const ReusedProfileCheck = styled(TextV2)({
  display: 'flex',
  flexFlow: 'row nowrap',
  alignItems: 'center',
  justifyContent: 'flex-start',
  svg: {
    marginLeft: '-2px',
  },
});

const StyledSpan = styled(TextV2)({});

interface LastSharedInfoProps {
  checked: boolean;
  disabled: boolean;
  editMode: boolean;
  cost: number;
}

const LastSharedInfo = styled('div', {
  shouldForwardProp: (prop) => prop !== 'checked' && prop !== 'editMode',
})<LastSharedInfoProps>(({ checked, disabled, editMode, cost, theme }) => {
  let background = theme.palette.grey[300];
  const disabledBackground = theme.palette.grey[200];
  let border = theme.palette.blue.main;

  if (editMode && checked) {
    border = theme.palette.blue.dark;
  }

  if (disabled) {
    background = disabledBackground;
    border = 'transparent !important';
  }

  const paddingValue =
    cost <= 0 || cost
      ? `1.5rem ${theme.spacing(spacingSizeMap.S)}`
      : `${theme.spacing(spacingSizeMap.XS)} ${theme.spacing(
          spacingSizeMap.M,
        )}`;

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'left',
    width: '100%',
    height: theme.spacing(spacingSizeMap.L),
    marginTop: -theme.spacing(spacingSizeMap.L),
    padding: paddingValue,
    color: theme.palette.grey[600],
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '0.875rem',
    backgroundColor: background,
    borderRight: `2px solid ${border}`,
    borderBottom: `2px solid ${border}`,
    borderLeft: `2px solid ${border}`,
    borderRadius: '0 0 4px 4px',
  };
});

const LastSharedDate = styled('span')({
  marginLeft: '5px',
});

const InviteeDFCCard = ({
  id,
  fullName,
  email,
  editMode,
  checked,
  setChecked,
  className,
  disabled = false,
  pronoun,
  dialect,
  showPronounsAndDialect,
  showLastSharedDate = false,
  profileLastSentAt = undefined,
  contributors,
  showContributorsIncluded = false,
  contributorsIncluded = NaN,
  showNewContributors = false,
  newContributors = NaN,
  showCost = false,
  cost = NaN,
}: DfcProps) => {
  const { t } = useTranslation();
  const globalTheme = useTheme();

  return (
    <>
      <StyledCard
        variant="outlined"
        sx={(theme) => {
          const disabledBackground = theme.palette.grey[200];
          let background;
          let border;
          let borderBottom;
          let borderRadius;
          let height;

          if (editMode && checked) {
            background = theme.palette.blue.light;
            border = theme.palette.blue.dark;
          }

          if (disabled) {
            background = disabledBackground;
            border = 'transparent !important';
          }

          if ((showLastSharedDate && profileLastSentAt) || showCost) {
            borderBottom = `0px`;
            borderRadius = '4px 4px 0px 0px';
            height = theme.spacing(spacingSizeMap.L * 8);
          } else {
            borderBottom = `2px solid ${theme.palette.blue.main}`;
            borderRadius = '4px';
            height = theme.spacing(spacingSizeMap.L * 9);
          }

          return {
            height,
            padding: `0 ${theme.spacing(spacingSizeMap.XS)} ${theme.spacing(
              spacingSizeMap.S,
            )} ${theme.spacing(spacingSizeMap.XS)}`,
            backgroundColor: background,
            borderColor: border,
            borderRadius,
            borderBottom,
          };
        }}
        className={className}
      >
        <CardContent
          sx={(theme) => ({
            paddingBottom: theme.spacing(spacingSizeMap.XS),
          })}
        >
          <Grid
            container
            wrap="nowrap"
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Grid sx={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>
              <TextV2 component="p" variant="bodyBold">
                {fullName}
              </TextV2>
              <TextV2
                component="p"
                color="textSecondary"
                aria-label={`invitee-card-label-email-${email}`}
              >
                {email}
              </TextV2>
            </Grid>
            <Grid>
              {editMode ? (
                <Checkbox
                  onChange={(e) => setChecked(e.target.checked)}
                  checked={checked}
                  name={id}
                  color="primary"
                  disabled={disabled}
                  slotProps={{
                    input: {
                      'aria-label': fullName,
                    },
                  }}
                />
              ) : (
                <Chip label="green" />
              )}
            </Grid>
          </Grid>
          {showPronounsAndDialect && pronoun && dialect && (
            <PronounsDialectLabel pronoun={pronoun} dialect={dialect} />
          )}
        </CardContent>
        <Divider />
        <CardContent>
          {contributors && contributors?.contributersTotal > 0 && (
            <TextV2 component="p">
              {t('ui.event-management.events.download-profiles.dfc.completed', {
                readyToPurchase: contributors?.contributorsCompleted,
                totalContributors: contributors?.contributersTotal,
              })}
            </TextV2>
          )}
          {showContributorsIncluded && (
            <TextV2 component="p" variant="bodyBold">
              {t(
                'ui.event-management.events.download-profiles.dfc.included-contributors',
                { count: contributorsIncluded },
              )}
            </TextV2>
          )}
          {showNewContributors && newContributors && (
            <ReusedProfileCheck
              component="p"
              variant="bodyBold"
              sx={{
                color: `${globalTheme.palette.success.main} !important`,
              }}
            >
              {t(
                'ui.event-management.events.purchase-profiles.dfc.new-contributor',
                {
                  count: newContributors,
                },
              )}
            </ReusedProfileCheck>
          )}
        </CardContent>
        <Box
          sx={{
            mt: 5,
            mb: 5,
          }}
        />
      </StyledCard>
      {showCost && (
        <LastSharedInfo
          checked={checked}
          disabled={disabled}
          editMode={editMode}
          cost={cost}
        >
          {cost > 0 && (
            <LastSharedDate>
              <Trans
                i18nKey="ui.event-management.events.purchase-profiles.purchase-cost.bold"
                components={{
                  bold: <TextV2 component="span" variant="bodyBold" />,
                }}
                values={{
                  cost,
                }}
              />
            </LastSharedDate>
          )}
          {cost <= 0 && (
            <LastSharedDate>
              <StyledSpan
                component="span"
                variant="bodyBold"
                color="textSecondary"
                sx={{ fontSize: '0.875rem !important' }}
              >
                {t(
                  'ui.event-management.events.purchase-profiles.purchase-no-cost.bold',
                )}
              </StyledSpan>{' '}
              <StyledSpan
                component="span"
                variant="bodyBold"
                color="textPrimary"
              >
                {t(
                  'ui.event-management.events.purchase-profiles.purchase-no-unit-cost.bold',
                  { cost },
                )}
              </StyledSpan>{' '}
              <StyledSpan
                component="span"
                variant="bodyBold"
                color="textSecondary"
              >
                {t(
                  'ui.event-management.events.purchase-profiles.purchase-profile-no-cost.bold',
                )}
              </StyledSpan>
            </LastSharedDate>
          )}
        </LastSharedInfo>
      )}
      {showLastSharedDate && profileLastSentAt && (
        <LastSharedInfo
          checked={checked}
          disabled={disabled}
          editMode={editMode}
          cost={cost}
        >
          {t('ui.event-management.invite-list.profile-last-shared', {
            profileLastSentAt: formatDateStyle2(profileLastSentAt),
          })}
        </LastSharedInfo>
      )}
    </>
  );
};

export default InviteeDFCCard;
